import { useRef } from "react";

import { useVirtualizer } from "@tanstack/react-virtual";

import type { IRegDataComparisonResult } from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";

import { WasteReturnRegFieldKeys } from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";

import type { ReconciliationGridCardModel, ReconciliationGridRow } from "./reconciliationGridTypes";

import { RECONCILIATION_GRID_ROW_HEIGHT } from "./reconciliationGridTypes";

import { GeographyLifecycleMessages, RegDataChangeTypes } from "./regDataAlignmentDisplay";

import { EwcRegDataComparisonRow } from "./EwcRegDataComparisonRow";

import { DrRegDataComparisonRow, drFlagsFromParsed } from "./DrRegDataComparisonRow";

import { FacilityOperationRegDataComparisonRow } from "./FacilityOperationRegDataComparisonRow";

import { GeographyRegDataComparisonRow } from "./GeographyRegDataComparisonRow";

import { StructuredRegDataComparisonRow } from "./StructuredRegDataComparisonRow";

import { ReconciliationRowAction } from "./ReconciliationRowAction";

import { HazardousMismatchAlert, DrFlagMismatchAlert } from "./ReconciliationMismatchAlerts";

import {

    buildSystemDisplayProps,

    getSoftDeleteUsageHintsForRow,

    isTemplateColumnPending,

    PENDING_CHANGE_TEXT_CLASS,

    shouldShowSoftDeleteUsageHints,

} from "./reconciliationRowVisualState";

import { getWarningsForStagedChange } from "./regDataAlignmentWarnings";

import "./ReconciliationGrid.css";

const ACTION_COLUMN_CLASS = "d-flex justify-content-center";

function systemRowTextClass(

    isDeleted: boolean,

    isActive: boolean,

    suppressLifecycleWarning: boolean

): string {

    if (suppressLifecycleWarning) return "";

    if (isDeleted) return "text-danger";

    if (!isActive) return "text-warning";

    return "";

}



export interface ReconciliationGridProps {

    card: ReconciliationGridCardModel;

    comparison: IRegDataComparisonResult | null;

    onStageRow: (row: ReconciliationGridRow) => void;

    onUndoStaged?: (changeKey: string) => void;

    onEditedValueChange?: (changeKey: string, editedValue: Record<string, unknown>) => void;

}



export function ReconciliationGrid({

    card,

    comparison,

    onStageRow,

    onUndoStaged,

    onEditedValueChange,

}: ReconciliationGridProps) {

    const parentRef = useRef<HTMLDivElement>(null);

    const rows = card.gridRows;



    const virtualizer = useVirtualizer({

        count: rows.length,

        getScrollElement: () => parentRef.current,

        estimateSize: () => RECONCILIATION_GRID_ROW_HEIGHT,

        getItemKey: (index) => rows[index].rowId,

        overscan: 8,

    });



    if (rows.length === 0) {

        return <div className="text-muted small">No values to reconcile.</div>;

    }



    const handleStage = (row: ReconciliationGridRow) => {

        if (row.actionDisabled || !row.stageChangeType) return;

        onStageRow(row);

    };



    return (

        <div className="reconciliation-grid">

            <div className="reconciliation-grid-h-scroll">

                <div className="reconciliation-grid-layout">

                    <div className="reconciliation-grid-header small fw-semibold text-muted">

                        <div>Template Data</div>

                        <div className="reconciliation-grid-system-header">System Data</div>

                        <div className={ACTION_COLUMN_CLASS}>Action</div>

                    </div>



                    <div ref={parentRef} className="reconciliation-grid-body">

                        <div style={{ height: virtualizer.getTotalSize(), position: "relative", width: "100%" }}>

                            {virtualizer.getVirtualItems().map((virtualRow) => {

                                const row = rows[virtualRow.index];

                                return (

                                    <div

                                        key={row.rowId}

                                        data-index={virtualRow.index}

                                        ref={virtualizer.measureElement}

                                        style={{

                                            position: "absolute",

                                            top: 0,

                                            left: 0,

                                            width: "100%",

                                            transform: `translateY(${virtualRow.start}px)`,

                                        }}

                                    >

                                        <ReconciliationGridRowView

                                            row={row}

                                            comparison={comparison}

                                            onStage={handleStage}

                                            onUndoStaged={onUndoStaged}

                                            onEditedValueChange={onEditedValueChange}

                                        />

                                    </div>

                                );

                            })}

                        </div>

                    </div>

                </div>

            </div>

        </div>

    );

}



interface ReconciliationGridRowViewProps {

    row: ReconciliationGridRow;

    comparison: IRegDataComparisonResult | null;

    onStage: (row: ReconciliationGridRow) => void;

    onUndoStaged?: (changeKey: string) => void;

    onEditedValueChange?: (changeKey: string, editedValue: Record<string, unknown>) => void;

}



function ReconciliationGridRowView({

    row,

    comparison,

    onStage,

    onUndoStaged,

    onEditedValueChange,

}: ReconciliationGridRowViewProps) {

    const displayProps = buildSystemDisplayProps(row);

    const option = row.systemSide.option;

    const systemTextClass =

        option != null

            ? systemRowTextClass(

                  option.isDeleted,

                  option.isActive,

                  displayProps.suppressLifecycleWarning

              )

            : "";

    const pendingClass = displayProps.pendingChange ? PENDING_CHANGE_TEXT_CLASS : "";

    const deleteClass = displayProps.markedForDelete ? "text-danger text-decoration-line-through" : "";

    const systemColumnClass = [systemTextClass, pendingClass, deleteClass].filter(Boolean).join(" ");



    return (

        <div className="reconciliation-grid-row">

            <div className={`pe-2 ${isTemplateColumnPending(row) ? PENDING_CHANGE_TEXT_CLASS : ""}`}>

                <TemplateCell row={row} />

            </div>

            <div className={`pe-2 ${systemColumnClass}`}>

                <SystemCell

                    row={row}

                    comparison={comparison}

                    displayProps={displayProps}

                    onEditedValueChange={onEditedValueChange}

                />

            </div>

            <div className={`${ACTION_COLUMN_CLASS} action-column-member`}>

                <ReconciliationRowAction row={row} onStage={onStage} onUndoStaged={onUndoStaged} />

            </div>

        </div>

    );

}



function TemplateCell({ row }: { row: ReconciliationGridRow }) {

    if (!row.templateSide.templateOption) {

        return <span className="text-muted small">—</span>;

    }



    const parsed = row.templateSide.templateOption.parsed ?? {};



    return (

        <div>

            <TemplateRowContent row={row} parsed={parsed} />

            {row.lifecycleNote && <div className="small text-muted mt-1">{row.lifecycleNote}</div>}

            <HazardousMismatchAlert warnings={row.warnings} />

            <DrFlagMismatchAlert warnings={row.warnings} />

        </div>

    );

}



function SoftDeleteUsageHints({

    row,

    comparison,

}: {

    row: ReconciliationGridRow;

    comparison: IRegDataComparisonResult | null;

}) {

    if (!shouldShowSoftDeleteUsageHints(row)) return null;



    const hints = getSoftDeleteUsageHintsForRow(row, comparison);

    if (hints.length === 0) return null;



    return (

        <div className="small text-warning mt-1">

            {hints.map((hint, index) => (

                <div key={`${hint.message}-${index}`}>{hint.message}</div>

            ))}

        </div>

    );

}



interface SystemCellProps {

    row: ReconciliationGridRow;

    comparison: IRegDataComparisonResult | null;

    displayProps: ReturnType<typeof buildSystemDisplayProps>;

    onEditedValueChange?: (changeKey: string, editedValue: Record<string, unknown>) => void;

}



function SystemCell({ row, comparison, displayProps, onEditedValueChange }: SystemCellProps) {

    const isTemplateOnlyAdd =

        row.rowState === "TemplateOnly" &&

        row.isStaged &&

        row.stagedChange?.changeType === RegDataChangeTypes.AddNewFromTemplate;



    if (!row.systemSide.option && !isTemplateOnlyAdd) {

        return <span className="text-muted small">—</span>;

    }



    const stagedWarnings =

        row.stagedChange && comparison

            ? getWarningsForStagedChange(row.stagedChange, comparison)

            : [];



    return (

        <div>

            <SystemRowContent

                row={row}

                displayProps={displayProps}

                stagedWarnings={stagedWarnings}

                onEditedValueChange={onEditedValueChange}

            />

            {row.lifecycleNote && row.templateSide.templateOption == null && !displayProps.pendingChange && (

                <div className="small text-muted mt-1">{row.lifecycleNote}</div>

            )}

            {displayProps.useInlineEdit &&

                row.fieldKey === WasteReturnRegFieldKeys.Geography &&

                row.stagedChange?.editedValue && (

                    <GeographyAddIncompleteNote editedValue={row.stagedChange.editedValue} />

                )}

            <SoftDeleteUsageHints row={row} comparison={comparison} />

        </div>

    );

}



function GeographyAddIncompleteNote({ editedValue }: { editedValue: Record<string, unknown> }) {

    const code = String(editedValue?.code ?? "").trim();

    const description = String(editedValue?.description ?? "").trim();

    const type = String(editedValue?.type ?? "").trim();

    if (code && description && type) return null;



    return <div className="small text-muted mt-1">{GeographyLifecycleMessages.stagedAddIncomplete}</div>;

}



interface SystemRowContentProps {

    row: ReconciliationGridRow;

    displayProps: ReturnType<typeof buildSystemDisplayProps>;

    stagedWarnings: ReturnType<typeof getWarningsForStagedChange>;

    onEditedValueChange?: (changeKey: string, editedValue: Record<string, unknown>) => void;

}



function SystemRowContent({ row, displayProps, stagedWarnings, onEditedValueChange }: SystemRowContentProps) {

    if (displayProps.useInlineEdit && displayProps.editedValue && row.stagedChangeKey) {

        const handleChange = (editedValue: Record<string, unknown>) => {

            onEditedValueChange?.(row.stagedChangeKey!, editedValue);

        };



        if (row.fieldKey === WasteReturnRegFieldKeys.Ewc) {

            return (

                <EwcRegDataComparisonRow

                    mode="edit"

                    editedValue={displayProps.editedValue}

                    warnings={stagedWarnings}

                    onChange={handleChange}

                />

            );

        }



        if (row.fieldKey === WasteReturnRegFieldKeys.Geography) {

            return (

                <GeographyRegDataComparisonRow

                    mode="edit"

                    editedValue={displayProps.editedValue}

                    onChange={handleChange}

                />

            );

        }



        if (row.fieldKey === WasteReturnRegFieldKeys.FacilityOperation) {

            return (

                <FacilityOperationRegDataComparisonRow

                    mode="edit"

                    editedValue={displayProps.editedValue}

                    onChange={handleChange}

                />

            );

        }



        return (

            <StructuredRegDataComparisonRow

                mode="edit"

                editedValue={displayProps.editedValue}

                onChange={handleChange}

            />

        );

    }



    const option = row.systemSide.option;



    if (row.fieldKey === WasteReturnRegFieldKeys.Ewc) {

        const isHazardous =

            displayProps.isHazardous ?? (option?.structured?.isHazardous === true);

        return (

            <EwcRegDataComparisonRow

                mode="display"

                displayCode={displayProps.displayCode ?? row.normalizedKey}

                displayDescription={displayProps.displayDescription ?? ""}

                isHazardous={isHazardous}

                descriptionPlacement="below"

            />

        );

    }



    if (row.fieldKey === WasteReturnRegFieldKeys.Geography) {

        return (

            <GeographyRegDataComparisonRow

                mode="display"

                displayCode={displayProps.displayCode}

                displayDescription={displayProps.displayDescription}

                displayType={displayProps.displayType}

            />

        );

    }



    if (row.fieldKey === WasteReturnRegFieldKeys.FacilityOperation) {

        return (

            <FacilityOperationRegDataComparisonRow

                mode="display"

                displayCode={displayProps.displayCode?.trim() ? displayProps.displayCode : undefined}

                displayDescription={displayProps.displayDescription ?? ""}

            />

        );

    }



    if (row.fieldKey === WasteReturnRegFieldKeys.EaDr) {

        const flags =

            displayProps.isForReceipt != null || displayProps.isForRemoval != null

                ? {

                      isForReceipt: displayProps.isForReceipt ?? false,

                      isForRemoval: displayProps.isForRemoval ?? false,

                  }

                : drFlagsFromParsed(option?.structured);

        return (

            <DrRegDataComparisonRow

                displayCode={displayProps.displayCode?.trim() ? displayProps.displayCode! : row.normalizedKey}

                isForReceipt={flags.isForReceipt}

                isForRemoval={flags.isForRemoval}

                displayDescription={displayProps.displayDescription ?? option?.description ?? undefined}

                descriptionPlacement="below"

                pendingChange={displayProps.pendingChange}

            />

        );

    }



    if (displayProps.displayCode?.trim() || displayProps.displayDescription?.trim()) {

        return (

            <StructuredRegDataComparisonRow

                mode="display"

                displayCode={displayProps.displayCode?.trim() ? displayProps.displayCode : undefined}

                displayDescription={displayProps.displayDescription ?? undefined}

                rawValue={displayProps.rawDisplayValue ?? option?.displayValue}

                descriptionPlacement="below"

            />

        );

    }



    return <div className="small">{displayProps.rawDisplayValue ?? option?.displayValue ?? "—"}</div>;

}



function TemplateRowContent({ row, parsed }: { row: ReconciliationGridRow; parsed: Record<string, unknown> }) {

    const raw = row.templateSide.templateRawValue ?? "";



    if (row.fieldKey === WasteReturnRegFieldKeys.Ewc) {

        return (

            <EwcRegDataComparisonRow

                mode="display"

                displayCode={String(parsed.code ?? raw)}

                displayDescription={String(parsed.description ?? "")}

                isHazardous={row.hazardIndeterminate ? undefined : Boolean(parsed.isHazardous)}

                hazardIndeterminate={row.hazardIndeterminate}

                warnings={row.warnings}

            />

        );

    }



    if (row.fieldKey === WasteReturnRegFieldKeys.Geography) {

        return (

            <GeographyRegDataComparisonRow

                mode="display"

                displayCode={String(parsed.code ?? "")}

                displayDescription={String(parsed.description ?? raw)}

                displayType={String(parsed.type ?? "")}

            />

        );

    }



    if (row.fieldKey === WasteReturnRegFieldKeys.FacilityOperation) {

        return (

            <FacilityOperationRegDataComparisonRow

                mode="display"

                displayCode={parsed.code != null ? String(parsed.code) : undefined}

                displayDescription={String(parsed.description ?? raw)}

            />

        );

    }



    if (row.fieldKey === WasteReturnRegFieldKeys.EaDr) {

        const flags = drFlagsFromParsed(parsed);

        return (

            <DrRegDataComparisonRow

                displayCode={String(parsed.code ?? raw)}

                isForReceipt={flags.isForReceipt}

                isForRemoval={flags.isForRemoval}

                displayDescription={parsed.description != null ? String(parsed.description) : undefined}

                pendingChange={isTemplateColumnPending(row)}

            />

        );

    }



    return (

        <StructuredRegDataComparisonRow

            mode="display"

            displayCode={parsed.code != null ? String(parsed.code) : undefined}

            displayDescription={parsed.description != null ? String(parsed.description) : undefined}

            rawValue={raw}

        />

    );

}


